<template>
    <div
        class="form-group custom-combobox"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
        role="combobox"
        :aria-busy="loading ? 'true' : 'false'"
    >
        <label :for="id" v-if="label">{{ label }}</label>
        <!-- eslint-disable -->
        <!-- Hier wird das doppelte "autocomplete" Attribut angemeckert, deshalb deaktivieren wir eslint -->
        <input
            :id="id"
            type="search"
            class="form-control"
            :class="inputClass"
            :value="value"
            @keydown="onKeyNavigation"
            @input="onInput"
            v-bind="$attrs"
            autocomplete="never"
            aria-autocomplete="list"
            :autocomplete="autocomplete"
            :aria-expanded="isSuggestVisible ? 'true' : 'false'"
            :aria-controls="`${id}__list`"
            :aria-busy="loading ? 'true' : 'false'"
            :aria-activedescendant="isSuggestVisible && focusedOption >= 0 ? `${id}__option-${focusedOption}` : null"
        />
        <!-- Screenreader-Live-Region für Ladehinweis -->
        <div aria-live="polite" class="sr-only">
            <span v-if="loading">Lade Vorschläge…</span>
        </div>
        <div
            v-show="isSuggestVisible"
            :id="`${id}__list`"
            class="suggest"
            role="listbox"
        >
            <ul>
                <li v-if="loading" class="loader">
                    <small-loader />
                    Lade
                </li>
                <template v-else>
                    <li v-if="$slots.prependOption" role="option">
                        <slot name="prependOption" />
                    </li>
                    <li
                        v-for="(option, index) in options"
                        :key="index"
                        :id="`${id}__option-${index}`"
                        @click.prevent="onOptionClick(option, index)"
                        :class="{active: index === focusedOption}"
                        role="option"
                    >
                        {{ option.label }}
                    </li>
                </template>
            </ul>
        </div>
        <slot />
    </div>
</template>
<script>
import SmallLoader from "@/components/Loader/SmallLoader";

export default {
    name: 'ComboBox',
    components: {SmallLoader},
    inheritAttrs: false,
    props: {
        id: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            required: false,
            default: '',
        },
        value: {
            type: [String, Number],
            required: false,
            default: '',
        },
        options: {
            type: Array,
            required: false,
            default: () => [],
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        requireSelection: { // wether or not to require a selection item (like a select box)
            type: Boolean,
            required: false,
            default: false,
        },
        inputClass: {
            type: [String, Object],
            required: false,
        },
        autocomplete: {
            type: String,
            default: 'never',
        },
    },
    data: () => ({
        isFocused: false,
        isSelected: false, // wether a value got already selected (resets on focus)

        // keyboard navigation
        focusedOption: -1,
    }),
    computed: {
        isSuggestVisible() {
            return this.isFocused && !this.isSelected && (this.loading || !!this.$slots.prependOption || this.options.length > 0);
        },
    },
    methods: {
        onInput(event) {
            this.isSelected = false;
            this.focusedOption = -1;
            this.$emit('input', event.target.value);
        },
        onKeyNavigation(event) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.focusedOption++;
                if (this.focusedOption >= this.options.length) {
                    this.focusedOption = 0;
                }
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.focusedOption--;
                if (this.focusedOption < 0) {
                    this.focusedOption = this.options.length - 1;
                }
            } else if (event.key === 'Enter' || event.key === 'Tab') {
                if (this.options[this.focusedOption]) {
                    this.onOptionClick(this.options[this.focusedOption], this.focusedOption);
                } else {
                    // noinspection EqualityComparisonWithCoercionJS
                    const index = this.options.findIndex(option => option.value == this.value);
                    if (index !== -1) {
                        this.onOptionClick(this.options[index], index);
                    }
                }
            } else if (event.key === 'Escape') {
                this.focusedOption = -1;
                this.isSuggestVisible = false;
            }
        },
        onOptionClick(option, index) {
            this.focusedOption = index;
            this.$emit('input', '' + option.value);
            this.$emit('select-item', option);
            this.isSelected = true;
        },
        onFocusIn() {
            this.isFocused = true;
            this.isSelected = this.options.some(option => option.value === this.value);
        },
        onFocusOut: function () {
            // setTimeout-Fix für Safari, sonst wird der Vorlieferant nicht in das Input-Feld übernommen
            setTimeout(() => {
                if (this.$el.matches(':focus-within')) {
                    return;
                }
                this.isFocused = false;
                if (this.requireSelection) {
                    if (!this.isSelected) {
                        this.$emit('input', '');
                    }
                }
            }, 100);
        }
    },
    watch: {
        options: function() {
            this.focusedOption = -1;
        }
    }
}
</script>
